<script lang="ts" setup>
  import { z } from 'zod'
  import { PHONE_NUMBER_ERROR } from '~/constants/errorMessage'
  import { privacyPolicyLink, termsAndConditionsRelishLink, madBundayTermsAndConditionsLink } from '~/constants/links'
  import { PHONE_NUM_LENGTH } from '~/constants/profile'
  import { mobileNumberRegex, nameRegex } from '~/constants/regex'

  import { useAuthStore, useNavigationStore, useTrackStore, useUserStore } from '~/store'
  import type { RegisterRequest } from '~/types/api/data-contracts'
  import { StudentTypes } from '~/types/form'

  import {
    toMadBundayAflTeamSalesforceString,
    toMadBundayNrlTeamSalesforceString
  } from '~/utils/madBundayApiTranslation'
  import { enableMadBunday } from '~/constants/madBunday'
  import { reformatDateString } from '~/utils/string'
  import { useNativeHaptics } from '~/composables/nativeHaptics'
  import { createClientTrackingEventId } from '~/composables/canonicalTracking'

  const LazyMadBundayRegistrationForm = defineAsyncComponent(
    () => import('~/components/promotion/GMadBundayRegistrationForm/GMadBundayRegistrationForm.vue')
  )
  const LazyAlert = defineAsyncComponent(() => import('~/components/common/GAlert/GAlert.vue'))

  const vueRouter = useRouter()
  const authStore = useAuthStore()
  const userStore = useUserStore()
  const navigationStore = useNavigationStore()
  const trackStore = useTrackStore()
  const { isMobileMode, isWebMode } = useAppMode()
  const { navigateBackToSignUpForm, loginDetails, queries, linkQueryParams } = useJoinRelishModal()
  const { setChosenLeague, setChosenTeam, chosenLeague, chosenTeam } = useMadBundayForm()
  const { alertContent, isAlertVisible, onAlertDismiss: onGenericAlertDismiss } = useGenericAlert()
  const { showTwoFactorVerificationModal, twoFactorVerificationModalId } = useTwoFactorAuthenticationModal()

  var onAlertDismiss: () => void = onGenericAlertDismiss

  const props = withDefaults(
    defineProps<{
      modalId?: string
      isView?: boolean
    }>(),
    {
      modalId: undefined,
      isView: false
    }
  )

  const isLoading = ref(false)

  const today = new Date()
  const currentYear = today.getFullYear()

  const validationSchema = toTypedSchema(
    z.object({
      firstName: z
        .string()
        .regex(nameRegex, 'First name can only contain letters')
        .max(50)
        .min(1, { message: 'Required' }),
      lastName: z
        .string()
        .regex(nameRegex, 'Last name can only contain letters')
        .max(50)
        .min(1, { message: 'Required' }),
      phoneNumber: z
        .string()
        .regex(mobileNumberRegex, PHONE_NUMBER_ERROR.INVALID)
        .length(PHONE_NUM_LENGTH, { message: PHONE_NUMBER_ERROR.LENGTH }),
      dateOfBirth: z
        .string()
        .regex(/^$|^\d{2}\/\d{2}\/\d{4}$/, 'Please check your date of birth is following DD/MM/YYYY format')
        .refine(
          (date) => {
            if (!date) return true
            const [day, month, year] = date.split('/').map(Number)
            const dob = new Date(year, month - 1, day)

            const hundredYearsAgo = new Date(today.getFullYear() - 100, today.getMonth(), today.getDate())
            return dob <= today && dob >= hundredYearsAgo
          },
          `Please check your date of birth is between the years ${currentYear - 100} – ${currentYear}.`
        )
        .optional(),
      studentType: z.nativeEnum(StudentTypes).optional(),
      referralCode: z.string().optional(),
      termsAndConditions: z.literal<boolean>(true, { errorMap: (_, __) => ({ message: 'Required' }) })
    })
  )

  const { errors, handleSubmit, meta, values, setFieldValue } = useForm({
    validationSchema,
    initialValues: {
      firstName: '',
      lastName: '',
      phoneNumber: '',
      dateOfBirth: undefined, // Note: Vue/Nuxt throws an error when this is not a datestring
      studentType: undefined,
      referralCode: navigationStore.referralCode ?? '',
      termsAndConditions: false
    }
  })

  const isFormValid = computed<boolean>(() => meta.value.valid)

  function getSource(source?: string, promo?: string, referrer?: string): string {
    if (source === 'promo') {
      // Clean up promo string by removing special characters and spaces
      if (promo) {
        const cleanedPromo = promo
          .replace(/[^\w\s]/gi, '')
          .replace(/\s+/g, '')
          .toLowerCase()
        return `sign-up-promotion-${cleanedPromo}`
      } else {
        return 'sign-up-promotion'
      }
    } else if (referrer === 'mad-bunday') {
      return 'sign-up-mad-bunday'
    }
    return 'sign-up'
  }

  const onSubmit = handleSubmit(async () => {
    isLoading.value = true

    try {
      await doRegister()
    } catch (error: any) {
      const errorCode = getApiErrorCode(error)
      // If the error is a 412 error and the error code is 'TWO_FACTOR_AUTHENTICATION_REQUIRED', show the two factor authentication modal
      if (error?.status === 412 && errorCode === 'TWO_FACTOR_AUTHENTICATION_REQUIRED') {
        const deliveryMethod = error?.data?.details?.deliveryMethod
        const otpMessage = error?.data?.details?.maskedDestination
        if (deliveryMethod === 'email' || deliveryMethod === 'sms') {
          await showTwoFactorVerificationModal({
            source: 'sign-up',
            title: 'Enter code',
            message: otpMessage,
            showPasswordInput: false,
            transparentModalStyle: true,
            onSubmit: onOtpSubmit,
            onSuccessfulVerifyCode: async () => {
              return Promise.resolve()
            },
            onSignOut: () => {},
            onDismiss: () => {
              isLoading.value = false
            }
          })
        }
      } else {
        alertContent.value = {
          header: error?.data?.title || 'Oops. Unexpected Error.',
          message: error?.data?.message || 'Please check your connection or try again later.'
        }
        await useNativeHaptics().triggerVibrate()
        onAlertDismiss = onGenericAlertDismiss
        isAlertVisible.value = true
        console.log('Error trying to register user', error)
        isLoading.value = false
      }
    }
  })

  async function doRegister(verificationCode?: string) {
    let referrer: string | undefined = undefined
    if (vueRouter.currentRoute.value.query.r) {
      // Extract the referrer from the route query
      referrer = vueRouter.currentRoute.value.query.r.toString()
    } else if (linkQueryParams.value) {
      // Extract the referrer from the link query if opened as a modal from a promo link
      referrer = linkQueryParams.value.get('r') || undefined
    }

    const madBundayTags =
      chosenLeague.value && chosenTeam.value
        ? {
            madBunday: {
              code: chosenLeague.value,
              team:
                chosenLeague.value === 'AFL'
                  ? toMadBundayAflTeamSalesforceString(chosenTeam.value)
                  : toMadBundayNrlTeamSalesforceString(chosenTeam.value),
              source: getSource(queries.value?.source, queries.value?.promo, referrer)
            }
          }
        : undefined

    const utmCodes = trackStore.utmCodes ? { utm: trackStore.utmCodes } : undefined

    const tags: Record<string, Record<string, string>> = {
      ...madBundayTags,
      ...utmCodes
    }

    if (!values.firstName || !values.lastName || !values.phoneNumber) {
      return
    }

    const request: RegisterRequest = {
      emailAddress: loginDetails.value!.email,
      password: loginDetails.value!.password,
      givenName: values.firstName,
      familyName: values.lastName,
      mobileNumber: values.phoneNumber,
      dateOfBirth: values.dateOfBirth ? reformatDateString(values.dateOfBirth) : null, //change from dd/mm/yyyy to yyyy-mm-dd
      studentLevel: values.studentType,
      // TODO: preferredRestaurantId to be done in future sprint work
      preferredRestaurantId: 0,
      referrer: referrer,
      tags: madBundayTags || utmCodes ? tags : undefined,
      twoFactorAuthenticationCode: verificationCode
    }

    const accountDetails = await register(request)
    // dismiss the Two Factor Authentication modal, if showing
    const topModal = await modalController.getTop()
    if (twoFactorVerificationModalId && topModal?.id === twoFactorVerificationModalId) {
      await modalController.dismiss(undefined, undefined, twoFactorVerificationModalId)
    }

    authStore.signIn(accountDetails.session!, accountDetails.user!)
    const signUpEventId = createClientTrackingEventId('user_successfully_signed_up')
    await gaEventOnUserSuccessfullySignedUp(!!values.referralCode, signUpEventId)
    trackingEventOnUserSuccessfullySignedUp(
      {
        status: true,
        content_name: 'SignUp'
      },
      signUpEventId
    )

    // reset the navigation store related to referral code before sign in
    // this is to prevent watch on the home page from triggering navigation to the referral code page

    navigationStore.resetSignUpExperience()
    navigationStore.resetReferralCode()
    if (navigationStore.pushTo === '/profile/referrals') {
      navigationStore.resetPushNavigation()
    }

    // if a referral code was supplied, attempt to redeem it
    if (values.referralCode && accountDetails.user?.id) {
      try {
        await addReferral(accountDetails.user?.id, { code: values.referralCode })
        gaEventAddReferralSuccess()
      } catch (error: any) {
        // if the referral code was invalid, show an error
        var message = getApiErrorMessage(error) ?? 'Please check your referral code or try again later.'
        var title = getApiErrorTitle(error) ?? 'Oops. Invalid Referral Code.'

        alertContent.value = {
          header: title,
          message
        }
        onAlertDismiss = onReferralAlertDismiss
        gaEventAddReferralFail()
        await useNativeHaptics().triggerVibrate()
        isAlertVisible.value = true
        console.warn('Error trying to redeem referral code', message)
        return
      }
    }

    await onSuccessfulRegistration()
    return true
  }

  async function onOtpSubmit(verificationCode: string) {
    // Call register method again with the verification code
    // exceptions will be handled in the modal
    try {
      await doRegister(verificationCode)
    } catch (error: any) {
      alertContent.value = {
        header: error?.data?.title || 'Oops. Unexpected Error.',
        message: error?.data?.message || 'Please check your connection or try again later.'
      }
      await useNativeHaptics().triggerVibrate()
      onAlertDismiss = onGenericAlertDismiss
      isAlertVisible.value = true
      console.log('Error trying to register user', error)
      isLoading.value = false
    }
  }

  async function onReferralAlertDismiss() {
    // Dismiss the alert, and then continue with the successful registration without the referral code redemption
    isAlertVisible.value = false
    await onSuccessfulRegistration()
  }

  async function onSuccessfulRegistration() {
    // reset the referral code after successful sign up

    if (props.isView) {
      emitDidSignUpFromWebsite(queries.value?.source === 'marketing-site')
      await vueRouter.replace({ path: '/home' })
    } else {
      if (!userStore.hasOnboarded) {
        var postSignInRoute = await getPostSignInRoute(queries.value || {}, undefined)
        if (postSignInRoute) {
          await vueRouter.replace(postSignInRoute)
        }
      }
      await modalController.dismiss(undefined, undefined, props.modalId)
    }
    isLoading.value = false
  }

  onBeforeRouteLeave(async (to, _from) => {
    if (props.isView && to.path.startsWith('/sign-up')) {
      // back navigation event for view (page) mode
      gaEventSignUpSecondPageModalNavigateBack()
    }
  })

  function onClearReferralCode() {
    setFieldValue('referralCode', '')
    navigationStore.resetReferralCode()
  }
  function handleBackNavigation() {
    if (props.isView) {
      vueRouter.back()
    } else {
      navigateBackToSignUpForm()

      // back navigation event for modal mode
      gaEventSignUpSecondPageModalNavigateBack()
    }
  }
</script>

<template>
  <GPageHeader :back-button-func="handleBackNavigation" compact disable-box-shadow title="Welcome Fam" />

  <ion-content>
    <span class="body flex justify-center">I can smell the perks already</span>
    <form @submit.prevent.stop="onSubmit">
      <GInputField
        autocapitalize="words"
        clear-input
        label="First Name"
        mask-pattern="name"
        name="firstName"
        type="text"
      />

      <GInputField
        autocapitalize="words"
        clear-input
        label="Last Name"
        mask-pattern="name"
        name="lastName"
        type="text"
      />

      <GInputField
        :max-length="PHONE_NUM_LENGTH"
        clear-input
        label="Phone No. (eg. 04XXXXXXXX)"
        mask-pattern="numeric"
        name="phoneNumber"
        type="tel"
      />

      <GBirthdayFreebieBanner />

      <GDateInputField label="Date of Birth (DD/MM/YYYY)" name="dateOfBirth" />
      <span class="body-extra-small field-blurb ml-auto">Optional</span>

      <GStudentTypeSelectorField name="studentType" :student-type="null" />
      <span class="body-extra-small field-blurb ml-auto">Optional</span>

      <LazyMadBundayRegistrationForm
        v-if="enableMadBunday"
        :chosen-league="chosenLeague"
        :chosen-team="chosenTeam"
        @on-league-selected="setChosenLeague"
        @on-team-selected="setChosenTeam"
      />

      <div v-if="enableMadBunday" class="body-small mad-bunday-conditions-text">
        Your team is for life (it can't be changed later).
        <br />
        By choosing a team you agree to the
        <a :href="madBundayTermsAndConditionsLink" target="_blank">Mad Bunday Promotion T&Cs</a>
      </div>

      <template v-if="values.referralCode">
        <div class="referral-code">
          <GInputField
            id="codeInput"
            ref="codeInputRef"
            :max-length="20"
            autocomplete="off"
            autocapitalize="characters"
            input-mode="text"
            type="text"
            label="Referral Code"
            name="referralCode"
            :disabled="true"
          />
          <GButton label="Remove" variant="tertiary" @click="onClearReferralCode" />
        </div>
        <span v-if="navigationStore.signUpExperience?.referralHelperText" class="body-extra-small field-blurb">
          {{ navigationStore.signUpExperience?.referralHelperText }}
        </span>
      </template>

      <div class="terms-and-conditions">
        <GCheckboxField class="body" name="termsAndConditions">
          I agree to the
          <a :href="termsAndConditionsRelishLink" target="_blank" onclick="event.stopPropagation()">
            Relish Membership Terms
          </a>
          and to the collection and use of my personal information in accordance with
          <a :href="privacyPolicyLink" target="_blank" onclick="event.stopPropagation()">Grill'd's Privacy Policy</a>
        </GCheckboxField>
        <div class="terms-and-conditions-error">{{ errors.termsAndConditions }}</div>
      </div>
    </form>
    <div v-if="isWebMode">
      <GButton
        :disabled="!isFormValid || isLoading"
        :should-show-loading-animation="isLoading"
        animation-color="light"
        block
        label="Let's Go"
        @click="onSubmit"
      />
    </div>
  </ion-content>

  <ion-footer v-if="isMobileMode">
    <ion-toolbar class="footer-toolbar">
      <GButton
        :disabled="!isFormValid || isLoading"
        :should-show-loading-animation="isLoading"
        animation-color="light"
        block
        label="Let's Go"
        @click="onSubmit"
      />
    </ion-toolbar>
  </ion-footer>

  <LazyAlert
    :backdrop-dismiss="false"
    :buttons="[
      {
        text: 'Gotcha',
        handler: () => (isAlertVisible = false)
      }
    ]"
    :header="alertContent.header"
    :is-open="isAlertVisible"
    :message="alertContent.message"
    @did-dismiss="onAlertDismiss"
  />
</template>

<style lang="scss" scoped>
  .footer-toolbar {
    --padding-bottom: 30px;
    --padding-top: 0px;
  }

  ion-content {
    form {
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding: 24px 16px 0;
    }

    .field-blurb {
      color: var(--color-text-muted);
    }

    .referral-code {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 8px;
    }
    .terms-and-conditions {
      display: flex;
      flex-direction: column;
      justify-content: left;
      gap: 8px;
      padding: 12px 0;
      a {
        font-size: 16px;
        pointer-events: auto;
        font-weight: 400;
      }

      .terms-and-conditions-error {
        font-size: 12px;
        color: var(--color-alert-danger) !important;
      }
    }
  }

  .mad-bunday-conditions-text {
    color: var(--color-text-muted);
  }
</style>
